import {
  forwardRef,
  type HTMLAttributes,
  type ElementRef,
  type ComponentPropsWithoutRef,
} from 'react';
import {
  Root,
  Trigger,
  Close,
  Portal,
  Overlay,
  Content,
  Title,
  Description,
} from '@radix-ui/react-dialog';
import { X } from '@/components/icons/barrel';
import { cn } from '@/utils/cn';

const Dialog = Root;

const DialogTrigger = Trigger;

const DialogClose = Close;

const DialogContent = forwardRef<
  ElementRef<typeof Content>,
  ComponentPropsWithoutRef<typeof Content> & {
    hideCloseButton?: boolean;
    unstyled?: boolean;
    closeClassName?: string;
  }
>(
  (
    {
      className,
      children,
      hideCloseButton,
      unstyled,
      closeClassName,
      ...props
    },
    ref,
  ) => (
    <Portal>
      <Overlay
        className={cn(
          'fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        )}
      >
        <Content
          ref={ref}
          className={cn(
            'fixed inset-x-0 bottom-0 z-50 grid w-full gap-4 rounded-t-3xl outline-none duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom sm:inset-x-auto sm:bottom-auto sm:left-[50%] sm:top-[50%] sm:w-full sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-none sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:slide-out-to-left-1/2 sm:data-[state=closed]:slide-out-to-top-[48%] sm:data-[state=open]:slide-in-from-left-1/2 sm:data-[state=open]:slide-in-from-top-[48%]',
            !unstyled
              ? 'bg-background shadow-lg sm:max-w-3xl sm:border sm:border-border'
              : 'sm:max-w-max',
            className,
          )}
          {...props}
        >
          {children}
          {!hideCloseButton && (
            <Close
              className={cn(
                'absolute right-5 top-5 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-transparent focus:ring-offset-1 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground',
                closeClassName,
              )}
            >
              <X className="h-6 w-6" />
              <span className="sr-only">Close</span>
            </Close>
          )}
        </Content>
      </Overlay>
    </Portal>
  ),
);
DialogContent.displayName = Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col sm:flex-row sm:justify-end', className)}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = forwardRef<
  ElementRef<typeof Title>,
  ComponentPropsWithoutRef<typeof Title>
>(({ className, ...props }, ref) => (
  <Title
    ref={ref}
    className={cn(
      'text-xl font-semibold leading-none tracking-tight',
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = Title.displayName;

const DialogDescription = forwardRef<
  ElementRef<typeof Description>,
  ComponentPropsWithoutRef<typeof Description>
>(({ className, ...props }, ref) => (
  <Description
    ref={ref}
    className={cn('text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
